import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { CampaignDrilldownHero } from './components/CampaignDrilldownHero';
import { CampaignTimeseriesChart } from './components/CampaignTimeseriesChart';
import { CampaignDrilldownTabs } from './components/CampaignDrilldownTabs';
import { useCampaignSummary } from './hooks/useCampaignSummary';
import { EmptyState } from '../_shared/EmptyState';

export function CampaignDetailView() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data, loading, error, notFound } = useCampaignSummary(campaignId);

  function backToList() {
    // Preserve filter context.
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    navigate(`/performance/campaigns?${next.toString()}`);
  }

  if (notFound) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={backToList}
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft size={14} /> Back to campaigns
        </button>
        <EmptyState
          title="Campaign not found"
          description={`No campaign with ID ${campaignId} could be resolved.`}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
        Failed to load campaign: {error}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <button
        type="button"
        onClick={backToList}
        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft size={14} /> Back to campaigns
      </button>

      {loading || !data ? (
        <div className="space-y-3">
          <div className="h-8 w-1/3 rounded bg-slate-100 animate-pulse" />
          <div className="grid grid-cols-7 gap-3">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="h-20 rounded bg-slate-100 animate-pulse" />
            ))}
          </div>
        </div>
      ) : (
        <>
          <CampaignDrilldownHero summary={data} />
          <CampaignTimeseriesChart campaignId={campaignId!} />
          <CampaignDrilldownTabs campaignId={campaignId!} />
        </>
      )}
    </div>
  );
}

export default CampaignDetailView;
